import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { CONNECTION_STATUS_EVENT } from '../../lib/connectionStatus';

/**
 * Subtle icon-only top-bar indicator. Renders a small spinning amber icon ONLY
 * while a background poll is serving a resilient fallback (transient DB failover)
 * or failing; stays hidden when everything is healthy. Tracks per-source state so
 * it clears as soon as every reporting source reports healthy again. On the first
 * flip from healthy → degraded it shows a single reassuring toast.
 */
export function ConnectionStatusIndicator() {
  const [degraded, setDegraded] = useState(false);
  const sources = useRef({});
  const wasDegraded = useRef(false);

  useEffect(() => {
    const handler = (e) => {
      const { source, degraded: d } = e.detail || {};
      if (!source) return;
      sources.current[source] = !!d;
      const anyDegraded = Object.values(sources.current).some(Boolean);
      setDegraded(anyDegraded);
      if (anyDegraded && !wasDegraded.current) {
        toast.info('Briefly reconnecting to the server — your data will refresh in a moment.', {
          id: 'connection-degraded',
          duration: 4000,
        });
      }
      wasDegraded.current = anyDegraded;
    };
    window.addEventListener(CONNECTION_STATUS_EVENT, handler);
    return () => window.removeEventListener(CONNECTION_STATUS_EVENT, handler);
  }, []);

  if (!degraded) return null;

  return (
    <span
      role="status"
      title="Connection degraded — reconnecting. Some data may be briefly out of date."
      aria-label="Connection degraded, reconnecting"
      data-testid="connection-status-indicator"
      className="inline-flex items-center justify-center h-7 w-7 rounded-full text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20"
    >
      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
    </span>
  );
}
